/**
 * The manual (board learn-r1 A): /learn lists every recipe with a search box, /learn/<slug> shows one with its prompt
 * to copy. Recipes come from GET /api/learn/recipes (one source: the starter template's src/learn/manual). Founders,
 * teammates and admins reach it; it takes the api as a prop so either shell can mount it.
 */
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { codeParts, groups, recipePath, RecipeEnvelope, RecipeList, RECIPES_PATH, searchRecipes, type Recipe, type RecipeSummary } from '../../domain/learn'
import { ApiError, type Api } from '../../infra/api'
import { Button, Card, Chip, EmptyState, ErrorNote, Grid, Inline, Input, Link, PageHeader, Skeleton, Stack, Text } from '../../ui'
import { useLoad } from '../useLoad'

export function LearnManual({ api }: { api: Api }) {
  const slug = useParams()['*'] ?? ''
  return slug ? <RecipeView key={slug} api={api} slug={slug} /> : <ManualHome api={api} />
}

const reasonOf = (e: Error) =>
  e instanceof ApiError && e.code === 'manual_unavailable' ? "The manual isn't available right now. Try again in a minute." : e.message

function Loading() {
  return (
    <Stack gap="md" label="Loading">
      <Skeleton height="title" width="40%" />
      <Skeleton height="block" />
      <Skeleton height="block" />
    </Stack>
  )
}

function ManualHome({ api }: { api: Api }) {
  const list = useLoad(() => api.get(RECIPES_PATH, RecipeList), [api])
  const [query, setQuery] = useState('')
  const found = searchRecipes(list.data?.recipes ?? [], query)
  return (
    <Stack gap="lg">
      <PageHeader
        title="The manual"
        description="Short recipes for working with your agent. Each one is a sentence to copy into Paseo, what happens next, and what to say if it looks wrong. Practise them in your app's Learn tab."
      />
      <Input search aria-label="Search the manual" placeholder="Search: ship, Shopify, undo, keys…" value={query} onChange={(e) => setQuery(e.target.value)} />
      {list.error && !list.data ? (
        <ErrorNote reason={reasonOf(list.error)} onRetry={list.reload} />
      ) : !list.data ? (
        <Loading />
      ) : found.length === 0 ? (
        <EmptyState kind="filtered" title={`Nothing matches “${query}”.`} action={<Button size="sm" onClick={() => setQuery('')}>Show every recipe</Button>}>
          Try one word, like ship, screen or key.
        </EmptyState>
      ) : (
        groups(found).map((g) => (
          <Card key={g.key} title={g.title} count={g.items.length}>
            <Stack gap="md">
              <Text tone="muted">{g.sentence}</Text>
              {g.items.map((r) => (
                <RecipeLine key={r.slug} recipe={r} />
              ))}
            </Stack>
          </Card>
        ))
      )}
    </Stack>
  )
}

/** One line of the contents: number, title as a link, its summary, and Coming soon when it waits on a feature. */
export function RecipeLine({ recipe: r }: { recipe: RecipeSummary }) {
  return (
    <Stack gap="xs">
      <Inline gap="sm" align="baseline">
        <Text mono tone="faint" size="sm">
          {r.number}
        </Text>
        <Link to={`/learn/${r.slug}`}>
          <Text weight="head">{r.title}</Text>
        </Link>
        {r.coming && <Chip tone="mute">Coming soon</Chip>}
      </Inline>
      <Text tone="muted" size="sm">
        {r.summary}
      </Text>
    </Stack>
  )
}

/** Plain words, with `backticks` shown as code. */
function Words({ text, tone }: { text: string; tone?: 'muted' }) {
  return (
    <Stack gap="sm">
      {text.split(/\n\n+/).map((para, i) => (
        <Text key={i} as="p" tone={tone}>
          {codeParts(para).map((p, j) =>
            p.code ? (
              <Text key={j} mono size="sm">
                {p.text}
              </Text>
            ) : (
              p.text
            ),
          )}
        </Text>
      ))}
    </Stack>
  )
}

/** Copies `text`, then says so for a moment. If the browser refuses, it says so: the words stay on the page to copy by hand. */
function CopyButton({ text, label = 'Copy', variant = 'ink' }: { text: string; label?: string; variant?: 'ink' | 'soft' | 'ghost' }) {
  const [said, setSaid] = useState<'copied' | 'failed' | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  useEffect(() => () => clearTimeout(timer.current), [])
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setSaid('copied')
    } catch {
      setSaid('failed')
    }
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setSaid(null), 2000)
  }
  return (
    <Button size="sm" variant={variant} onClick={() => void copy()} aria-live="polite">
      {said === 'copied' ? 'Copied' : said === 'failed' ? 'Select and copy it' : label}
    </Button>
  )
}

function RecipeView({ api, slug }: { api: Api; slug: string }) {
  const one = useLoad(() => api.get(recipePath(slug), RecipeEnvelope), [api, slug])
  if (one.error) {
    if (one.error instanceof ApiError && one.error.status === 404) {
      return (
        <EmptyState title="No such recipe." action={<Link to="/learn">Back to the manual</Link>}>
          It may have been renamed. Every recipe is listed in the manual.
        </EmptyState>
      )
    }
    return <ErrorNote reason={reasonOf(one.error)} onRetry={one.reload} />
  }
  if (!one.data) return <Loading />
  return <RecipeBody recipe={one.data.recipe} />
}

function RecipeBody({ recipe: r }: { recipe: Recipe }) {
  return (
    <Stack gap="lg">
      <PageHeader
        crumb={{ label: r.group === 'crash-course' ? 'The manual · Crash course' : 'The manual', to: '/learn' }}
        title={`${r.number} · ${r.title}`}
        after={r.coming ? <Chip tone="mute">Coming soon</Chip> : undefined}
        description={r.summary}
      />
      {r.coming && (
        <Card>
          <Text tone="muted">{r.coming}</Text>
        </Card>
      )}
      <Card title="Say this to your agent" action={<CopyButton text={r.prompt} />}>
        <Text as="p" weight="read">
          “{r.prompt}”
        </Text>
      </Card>
      {r.why && (
        <Card title="Why it works">
          <Words text={r.why} />
        </Card>
      )}
      <Grid cols={2}>
        <Stack gap="md">
          <Card title="What happens">
            <Stack gap="sm">
              {r.steps.map((s, i) => (
                <Inline key={i} gap="sm" align="start" wrap={false}>
                  <Text mono tone="faint" size="sm">
                    {i + 1}
                  </Text>
                  <Words text={s} />
                </Inline>
              ))}
            </Stack>
          </Card>
          <Card title="It worked when">
            <Words text={r.worked} />
          </Card>
        </Stack>
        <Stack gap="md">
          <Card title="If it looks wrong">
            <Stack gap="md">
              {r.wrong.map((w) => (
                <Stack key={w.symptom} gap="xs">
                  <Text weight="head">{w.symptom}</Text>
                  <Inline gap="sm" align="start" justify="between">
                    <Text tone="muted">Say: “{w.say}”</Text>
                    <CopyButton text={w.say} variant="ghost" />
                  </Inline>
                </Stack>
              ))}
            </Stack>
          </Card>
          {r.skill && (
            <Card title={`Your agent's skill: ${r.skill}`}>
              <Stack gap="sm">
                {r.founder ? <Words text={r.founder} tone="muted" /> : null}
                <Text tone="faint" size="sm">
                  The same words your agent reads, in your app at .claude/skills/{r.skill}.
                </Text>
              </Stack>
            </Card>
          )}
          {r.missions.length > 0 && (
            <Card title="Practise it">
              <Stack gap="xs">
                <Text tone="muted">In your app's Learn tab, the page checks itself when it works:</Text>
                {r.missions.map((m) => (
                  <Text key={m.id}>
                    <Text mono tone="faint" size="sm">
                      {m.number}
                    </Text>{' '}
                    {m.title}
                  </Text>
                ))}
              </Stack>
            </Card>
          )}
        </Stack>
      </Grid>
      <Inline>
        <Link to="/learn">All recipes</Link>
      </Inline>
    </Stack>
  )
}
